import { findItemByIndex, getItemsLength, parseSuggestionsResponse, RichTextValue, SuggestionsGroupData, SuggestionsItemData } from "@xapp/chat-widget-core";
import React, { FC, useCallback, useContext, useEffect, useMemo, useState, VFC } from "react";
import { SuggestionTypes, Response } from "stentor-models";
import { StentorApi } from "../../api";
import { SearchWidgetConfig } from "../../config";
import { useSearchState, useSearchStore } from "../../store";
import { getActionLabel } from "../../utils/getActionLabel";
import { log } from "../../utils";
import { SearchWidgetConfigContext } from "../../utils/SearchWidgetConfigContext";
import { useLoading } from "../../utils/useLoading";
import { useSuggestionsFetch } from "../../utils/useSuggestionsFetch";
import { SearchInputContainer } from "../SearchInput";
import { SearchResponse } from "../SearchResponse";
import "./SearchWidget.scss";

export const AUDIO_WAIT_NOTHING = 0, AUDIO_WAIT_CLOSE = 1, AUDIO_WAIT_MINIMIZE = 2;

export interface SearchWidgetProps {
    readonly config: SearchWidgetConfig;
}

export const SearchWidget: FC<SearchWidgetProps> = props => {

    const { config } = props;

    return (
        <SearchWidgetConfigContext.Provider value={config}>
            <SearchWidgetRaw />
        </SearchWidgetConfigContext.Provider>
    );
}

const SearchWidgetRaw: VFC = () => {

    const [state, dispatch] = useSearchStore();

    const { loading, run } = useLoading();

    const { query, response, responseId /* responseQuery */ } = state;

    const [index, setIndex] = useState<number>(-1);

    const suggestionsResponse = useSuggestionsFetch(query.text, undefined);

    const suggestions = suggestionsResponse.state === "success" ? suggestionsResponse.data : undefined;

    const resultRaw = useMemo(() => parseSuggestionsResponse(suggestions, 1), [suggestions]);

    const [result, setResult] = useState<readonly SuggestionsGroupData[] | undefined>(resultRaw);

    const [audioOn, setAudioOn] = useState<boolean>(false);

    const [waitingOnAudioToFinish, setwaitingOnAudioToFinish] = useState<number>(AUDIO_WAIT_NOTHING);

    useEffect(() => {
        if (resultRaw) {
            setResult(resultRaw);
        }
    }, [resultRaw]);

    const length = useMemo(() => result ? getItemsLength(result) : 0, [result]);

    const currentSuggestion = useMemo<SuggestionsItemData | undefined>(() => result ? findItemByIndex(result, index) : undefined, [result, index]);

    const config = useContext(SearchWidgetConfigContext);

    const { active, autoGreeting } = useSearchState();

    const api = useMemo(() => {
        return StentorApi.getApi({
            key: config.api.key,
            url: config.api.url
        });
    }, [config]);

    const handleNext = useCallback(() => {
        setIndex(prev => {
            if (prev < 0) {
                return 0;
            }
            return (prev + 1) % length;
        });
    }, [length]);

    const handlePrev = useCallback(() => {
        setIndex(prev => {
            if (prev < 0) {
                return length - 1;
            }
            return (prev + length - 1) % length;
        });
    }, [length]);

    const handleCancel = useCallback(() => {
        dispatch({
            type: "new-search"
        });
        dispatch({
            type: "set-modal-state",
            active: false,
            minimized: true,
            autoGreeting: false
        });
    }, [dispatch]);

    // const launchRequest = useCallback(() => {
    //     const query = "Hello!";
    //     dispatch({
    //         query: {
    //             text: query,
    //             formats: []
    //         },
    //         type: "set-query"
    //     });
    //     api.resetSession();
    //     run(() => api.launchRequest(query).then(response => {
    //         dispatch({
    //             type: "set-response",
    //             response,
    //             query
    //         });
    //     }));
    // }, [api, dispatch, run]);

    const greetingRequest = useCallback(() => {
        const query = " ";

        const response: Response = {
            outputSpeech: {
                displayText: "Hello, where would you like to go?",
                ssml: "<speak><audio src=\"https://assets.xapp.media/prod/travelshift/tts/-1911387145_ssml_amznPolly_Emma_-473310894_22050.mp3\" /></speak>"
            }
        };

        dispatch({
            query: {
                text: query,
                formats: [],
            },
            type: "set-query",
        });

        api.resetSession();

        dispatch({
            type: "set-response",
            response,
            query,
        });
    }, [api, dispatch]);

    const intentRequest = useCallback((query: string) => {
        dispatch({
            query: {
                text: query,
                formats: []
            },
            type: "set-query"
        });
        run(() => api.intentRequest(query).then(response => {
            dispatch({
                type: "set-response",
                response,
                query
            });
        }));
    }, [api, dispatch, run]);

    const channelActionRequest = useCallback((data: any, action: string) => {
        api.channelActionRequest(data, action).then(response => {
            log(`ACTION Request (${action}). Data: ${JSON.stringify(data)} Response: ${JSON.stringify(response)}`);

            if (response.outputSpeech || response.displays) {
                dispatch({
                    type: "set-response",
                    response,
                    query: ""
                });
            }

            if (!!data.autoClose) {
                setwaitingOnAudioToFinish(AUDIO_WAIT_CLOSE);
            } else if (!!data.autoMinimize) {
                setwaitingOnAudioToFinish(AUDIO_WAIT_MINIMIZE);
            } else {
                setwaitingOnAudioToFinish(AUDIO_WAIT_NOTHING);
            }
        });
    }, [api, dispatch]);

    useEffect(() => {
        if (autoGreeting && active) {
            greetingRequest();
            dispatch({
                type: "set-modal-state",
                active: true,
                minimized: false,
                autoGreeting: false
            });
        }
    }, [autoGreeting, active, greetingRequest, dispatch]);

    useEffect(() => {
        if (!audioOn) {
            if (waitingOnAudioToFinish === AUDIO_WAIT_CLOSE) {
                dispatch({
                    type: "new-search"
                });
                dispatch({
                    type: "set-modal-state",
                    active: false,
                    minimized: false,
                    autoGreeting: false
                });
                setwaitingOnAudioToFinish(AUDIO_WAIT_NOTHING);
            } else if (waitingOnAudioToFinish === AUDIO_WAIT_MINIMIZE) {
                dispatch({
                    type: "new-search"
                });
                dispatch({
                    type: "set-modal-state",
                    active: false,
                    minimized: true,
                    autoGreeting: false
                });
                setwaitingOnAudioToFinish(AUDIO_WAIT_NOTHING);
            }
        }
    }, [audioOn]);

    const handleSubmit = useCallback((value: RichTextValue) => {
        if (currentSuggestion) {
            intentRequest(currentSuggestion.text)
        } else {
            intentRequest(value.text);
        }
    }, [intentRequest, currentSuggestion]);

    const handleSearch = useCallback((value: RichTextValue) => {
        if (!value.text) {
            dispatch({
                type: "new-search"
            });
        }
    }, [dispatch]);

    const handleActionClick = useCallback((item: SuggestionTypes) => {
        const text = getActionLabel(item);
        intentRequest(text);
    }, [intentRequest]);

    const handleActionResponse = useCallback((data: unknown, action: string) => {
        if (!action) {
            action = (data as any).action;
        }
        channelActionRequest(data, action);
    }, [channelActionRequest]);

    const handleAudioChange = (isOn: boolean) => {
        setAudioOn(isOn);
    };

    const displayContainer = !!response; // || !!query.text;

    return (
        <div className="xapp-search-widget">
            <SearchInputContainer
                autoFocus
                audioOn={audioOn}
                onPrev={handlePrev}
                onNext={handleNext}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                onSearch={handleSearch}
                loading={loading}
                outputSpeech={response?.outputSpeech}
            />
            {displayContainer && (
                <div className="xapp-search-widget__content">
                    <>
                        {response && (
                            <SearchResponse
                                response={response}
                                responseId={responseId}
                                className="xapp-search-widget__response"
                                onActionClick={handleActionClick}
                                onActionResponse={handleActionResponse}
                                onAudioChange={handleAudioChange}
                            />
                        )}
                    </>
                </div>
            )}

            {!!handleCancel && (
                <div className="xapp-search-widget__close" onClick={handleCancel}>{"\u2715"}</div>
            )}
        </div>
    );
};
